// CareBot 开通主按钮
// 接入位置：EnhancedResultsPage 的 NextActionsPanel 下方
//
// 职责简化（v2）：
// - 仅决定按钮文案（"一键开通" vs "已开通 · 查看二维码"），由 status 端点驱动
// - 不再做 onboard 调用，所有 onboard / SMS / 手机号收集逻辑均下沉到 Modal
// - 不再要求 elderPhone 必填：现场补填走 Modal 的 phone/sms 步骤
//
// 已激活态判定：
// - mount 时调 getOnboardingStatus(userId)；已激活则换文案，点击进 Modal 时由 Modal
//   幂等再调 onboard 取最新 elderClaim（claimUrl/expiresAt）
// - status 失败时降级当未激活，避免按钮永久禁用

import React, { useEffect, useState } from 'react';
import { Heart, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { carebotOnboardingService } from '../../services/carebotOnboardingService';
import { CareBotActivationModal } from './CareBotActivationModal';

interface CareBotActivationButtonProps {
  userId?: string | number;
  assessmentId?: string | number;
  elderName?: string;
  elderPhone?: string;       // 来自 user.phone，可空；空时由 Modal 引导补填
  needReportId?: string | number;
  className?: string;
}

function getDisabledReason(
  userId?: string | number,
  assessmentId?: string | number
): string | null {
  if (!userId) return '缺少老人档案信息，无法开通';
  if (!assessmentId) return '请先完成评估';
  return null;
}

export const CareBotActivationButton: React.FC<CareBotActivationButtonProps> = ({
  userId,
  assessmentId,
  elderName,
  elderPhone,
  needReportId,
  className,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [activated, setActivated] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const disabledReason = getDisabledReason(userId, assessmentId);
  const isDisabled = !!disabledReason;

  // mount / userId 变更时拉激活状态
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setStatusLoading(true);
    carebotOnboardingService
      .getOnboardingStatus(userId)
      .then((res) => {
        if (!cancelled) setActivated(res.activated);
      })
      .catch(() => {
        // 状态查询失败时降级为未激活，不阻塞顾问操作
        if (!cancelled) setActivated(false);
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const handleClick = () => {
    if (disabledReason) return;
    setModalOpen(true);
  };

  return (
    <>
      <div className={className}>
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 rounded-2xl p-5 lg:p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex-shrink-0 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-sm">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-800 flex items-center gap-1">
                  {activated ? '已开通 CareBot AI 陪伴' : '开通 CareBot AI 陪伴'}
                  <Sparkles className="w-4 h-4 text-amber-500" />
                </h3>
                <p className="text-xs text-gray-500">
                  {activated
                    ? '该老人已激活，可重新查看二维码或重发邀请'
                    : '把这次 ICOPE 评估同步给老人，30 天免费体验 AI 陪伴智能体'}
                </p>
              </div>
            </div>

            <div className="flex-1" />

            <div className="flex flex-col items-stretch sm:items-end gap-1">
              <Button
                onClick={handleClick}
                disabled={isDisabled || statusLoading}
                size="lg"
                className="min-w-[180px]"
                title={disabledReason || undefined}
              >
                <Heart className="w-5 h-5" />
                {activated ? '查看二维码' : '一键开通 CareBot'}
              </Button>
              {disabledReason && (
                <span className="text-xs text-amber-600">{disabledReason}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {userId !== undefined && assessmentId !== undefined && (
        <CareBotActivationModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          userId={userId}
          assessmentId={assessmentId}
          needReportId={needReportId}
          existingPhone={elderPhone || ''}
          elderName={elderName}
          onOnboardSuccess={() => setActivated(true)}
        />
      )}
    </>
  );
};

export default CareBotActivationButton;
